import { Typography } from "@mui/material";
import { get } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";

import { Form as FinalForm } from "react-final-form";
import { Route, Switch } from "react-router-dom";

import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import Toast from "src/components/Toast/Toast";
import CollapsibleCard from "../../../components/CollapsibleCard/CollapsibleCard";
import FormModal from "../../../components/FormModalV2/FormModal";
import * as S from "../../../constants/StringConstants";
import { INote } from "../../../store/models/shared.models";
import { IRootState } from "../../../store/reducers";
import {
  downloadAnalyzer,
  downloadAnalyzerProposal,
  downloadEqualizer,
  downloadEqualizerProposal,
} from "../../../store/reducers/documentation";
import { saveToDownloads } from "../../../util/util.fileService";
import Correspondence from "./Correspondence/ProposalCorrespondence";
import ProposalDetailsContainer from "./Details/ProposalDetailsContainer";
import { saveSymbol } from "./Details/Regular/ProposalBuilder";
import Documentation from "./Documentation/ProposalDocumentation";
import "./ProposalContainer.scss";

import { addProposalNote, editProposalNote, fetchProposalNotes } from "../../../store/reducers/proposalNotes";

import {
  addProposalRevision,
  fetchProposalRevisions,
  mapProposalRevisionToNote,
} from "../../../store/reducers/proposalRevisions";

import AppBar from "src/components/AppBar/AppBar";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import Icon, { IconName } from "src/components/Icon/Icon";
import { Tabs } from "src/components/NavTabs/Tabs";
import { UserRole } from "src/store/models/user.model";
import { removeProposal } from "src/store/reducers/proposals";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import NewNoteForm from "../../../components/NewNoteForm/NewNoteForm";
import ProposalRevisionItem from "../../../components/ProposalRevisionItem/ProposalRevisionItem";
import { fetchDisclaimer, updateDisclaimer } from "../../../store/reducers/proposalDisclaimer";
import AnalyzerLinks from "./Details/AnalyzerLinks";
import QuestionAnswersStatusTracker from "./Details/QuestionAnswersStatusTracker";

export interface Params {
  id: string;
}

export interface IProposalContainerProps extends StateProps, DispatchProps, RouteComponentProps<Params> {}

interface IProposalContainerState {
  realTimeNote?: string;
  cloneCounter: number;
  newRevisionPrompt: boolean;
  deleteProposalPrompt: boolean;
  loading: boolean;
  isToastOpen: boolean;
}

export class ProposalContainer extends React.Component<IProposalContainerProps, IProposalContainerState> {
  state = {
    realTimeNote: null,
    cloneCounter: 0,
    newRevisionPrompt: false,
    deleteProposalPrompt: false,
    loading: false,
    isToastOpen: false,
  };

  setLoading = (loading: boolean) => {
    this.setState({ loading });
  };

  componentDidMount() {
    const proposalID = get(this.props.match.params, "id", "new");
    document.title = S.MAIN_PROPOSAL_DETAILS(proposalID);

    if (proposalID !== "new") {
      this.props.fetchProposalNotes(proposalID);
      this.props.fetchProposalRevisions(proposalID);
    }

    this.props.fetchDisclaimer(proposalID);
  }

  componentWillUnmount() {
    document.title = S.MAIN_TITLE;
  }

  addNote = async (title: string, description: string) => {
    const proposalID = get(this.props.match.params, "id", "new");
    await this.props.addProposalNote(proposalID, title, description);
  };

  addRevision = async () => {
    const proposalID = get(this.props.match.params, "id", "new");
    await this.props.addProposalRevision(proposalID);
    setTimeout(() => {
      this.props.fetchProposalRevisions(proposalID);
    }, 2000);
  };

  editNote = async (id: number, title: string, description: string) => {
    const proposalID = get(this.props.match.params, "id", "new");
    await this.props.editProposalNote(proposalID, id, title, description);
  };

  brokerNoteToNoteModel = (brokerNote: string): INote => {
    return {
      id: 0,
      title: "",
      description: brokerNote,
    };
  };

  onBrokerChange = (notes: string) => {
    this.setState(state => ({
      ...state,
      realTimeNote: notes,
    }));
  };

  saveDisclaimer = async values => {
    const proposalID = get(this.props.match.params, "id", "new");
    if (proposalID !== "new") {
      const { disclaimer } = values;
      this.setLoading(true);
      try {
        await this.props.updateDisclaimer(proposalID, disclaimer);
      } finally {
        this.setLoading(false);
      }
    }
  };

  clone = () => {
    this.setState(prevState => ({
      cloneCounter: prevState.cloneCounter + 1,
    }));
  };

  render() {
    const {
      auth,
      history,
      proposalNotes,
      proposalMap,
      proposalRevisions,
      disclaimerMap,
      isSubmitting,
      isSavingClone,
      removeProposal,
    } = this.props;

    let proposalID = get(this.props.match.params, "id", "new");
    const isNew = proposalID === "new";
    const isML = this.props.match.params[0] === "ml";
    if (isNew && isML) {
      proposalID = "newml";
    }

    const { realTimeNote } = this.state;

    // Highlight Active Tab
    const pathNameRegex = /\/([a-zA-Z0-9]+)$/;
    const match = pathNameRegex.exec(this.props.location.pathname);
    const pathname = match ? match[1] : "";

    // Proposal Notes
    const notes = proposalNotes.map[proposalID];

    // Proposal Revisions
    const revisions = proposalRevisions.map[proposalID] || [];

    // Broker Notes
    const proposal = proposalMap[proposalID];
    const isMultiLife = get(proposal, "isMultiLife");
    const brokerNote = realTimeNote || get(proposal, "broker.notes");
    const isSubmitted = get(proposal, "isSubmitted");
    const brokerNotes = brokerNote ? [this.brokerNoteToNoteModel(brokerNote)] : [];

    // Disclaimer
    const disclaimer = disclaimerMap[proposalID];
    const disclaimerForm = {
      disclaimer,
    };

    const isAdmin = hasAnyAuthority(auth.user, [UserRole.Admin]);

    return (
      <div>
        <AppBar icon={IconName.Proposals} title={isNew ? S.PC_NEW_TITLE : S.PC_EXISTING_TITLE} />
        <div className="proposal__button-container">
          <div className="proposal__links">
            <Tabs
              pages={[
                { name: S.PC_DETAILS_TAB, path: "details" },
                { name: S.PC_DOCUMENTATION_TAB, path: "proposal" },
                { name: S.PC_CORRESPONDENCE_TAB, path: "documentation" },
              ]}
              pathname={pathname}
              isNew={isNew}
              id={proposalID}
              basePath="proposals"
              setChangeToURL={url => history.push(url)}
            />

            <div className="proposal__buttons">
              <ul>
                <li>
                  <button
                    className="button-v2__transparent button-v2--with-icon"
                    onClick={this.clone}
                    disabled={!proposalID || proposalID === "new"}>
                    {isSavingClone ? (
                      <Spinner color="dark" size={"sm"} />
                    ) : (
                      <>
                        <Icon name={IconName.CopyBlue} />
                        {S.PC_PROPOSAL_CLONE_BUTTON}
                      </>
                    )}
                  </button>
                </li>
                {proposal && proposal.isMultiLife ? (
                  <li>
                    <button
                      className="button-v2__transparent button-v2--with-icon"
                      onClick={async () => {
                        try {
                          const result = await this.props.downloadEqualizerProposal(proposalID);
                          const fileName = get(result, "value.headers.x-attachment-filename");
                          const data = get(result, "value.data");
                          if (fileName && data) {
                            saveToDownloads(fileName, "application/pdf", data);
                          }
                        } catch (e) {}
                      }}
                      disabled={!proposalID || !isSubmitted || proposalID === "new"}>
                      <Icon name={IconName.DownloadFileDarkBlue} size="24px" />
                      {S.PC_PROPOSAL_BUTTON}
                    </button>
                  </li>
                ) : (
                  <li>
                    <button
                      className="heading5 button-v2__transparent button-v2--with-icon"
                      onClick={async () => {
                        try {
                          const result = await this.props.downloadAnalyzerProposal(proposalID);
                          const fileName = get(result, "value.headers.x-attachment-filename");
                          const data = get(result, "value.data");
                          if (fileName && data) {
                            saveToDownloads(fileName, "application/pdf", data);
                          }
                        } catch (e) {}
                      }}
                      disabled={!proposalID || !isSubmitted || proposalID === "new"}>
                      <Icon name={IconName.DownloadFileDarkBlue} size="24px" />
                      {S.PC_PROPOSAL_BUTTON}
                    </button>
                  </li>
                )}
                {proposal && proposal.isMultiLife ? (
                  <li>
                    <button
                      className="button-v2__transparent button-v2--with-icon"
                      onClick={async () => {
                        try {
                          const result = await this.props.downloadEqualizer(proposalID);
                          const fileName = get(result, "value.headers.x-attachment-filename");
                          const data = get(result, "value.data");
                          if (fileName && data) {
                            saveToDownloads(fileName, "application/pdf", data);
                          }
                        } catch (e) {}
                      }}
                      disabled={!proposalID || !isSubmitted || proposalID === "new" || !isMultiLife}>
                      {S.PC_EQUALIZER_BUTTON}
                    </button>
                  </li>
                ) : (
                  <li>
                    <button
                      className="button-v2__transparent button-v2--with-icon"
                      onClick={async () => {
                        try {
                          const result = await this.props.downloadAnalyzer(proposalID);
                          const fileName = get(result, "value.headers.x-attachment-filename");
                          const data = get(result, "value.data");
                          if (fileName && data) {
                            saveToDownloads(fileName, "application/pdf", data);
                          }
                        } catch (e) {}
                      }}
                      disabled={!proposalID || !isSubmitted || proposalID === "new" || isMultiLife}>
                      <Icon name={IconName.DownloadFileDarkBlue} size="24px" />
                      {S.PC_ANALYZER_BUTTON}
                    </button>
                  </li>
                )}
                <li>
                  <button
                    className={`button-v2 button-v2__primary button-v2--with-icon ${isSubmitting ? "is-submitting" : ""}`}
                    onClick={e => {
                      // HACK: This should be in redux or something smarter. But we has no time :((
                      const page = window["currPage"];
                      const form = document.getElementById(`proposal-builder-form-${page}`);
                      if (form instanceof HTMLFormElement) {
                        window[saveSymbol] = true;
                        form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
                      }
                    }}>
                    <span className="btn-icon btn-icon--default">
                      <Icon name={IconName.Plus} />
                    </span>
                    <span className="btn-icon btn-icon--active">
                      <Icon name={IconName.PlusBlue} />
                    </span>
                    {isSubmitting ? <Spinner color="light" /> : S.PC_CREATE_BUTTON}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <main className="proposal__wrap">
          <section className="proposal__container">
            <Switch>
              <Route
                path="/proposals/:id/(ml)?/details"
                render={props => {
                  return (
                    <ProposalDetailsContainer
                      {...props}
                      proposalID={proposalID}
                      onBrokerChange={this.onBrokerChange}
                      cloneConter={this.state.cloneCounter}
                    />
                  );
                }}
              />
              <Route
                path="/proposals/:id/proposal"
                render={props => <Documentation {...props} proposalID={proposalID} />}
              />
              <Route
                path="/proposals/:id/documentation"
                render={props => <Correspondence {...props} proposalID={proposalID} />}
              />
            </Switch>
          </section>
          <aside>
            <CollapsibleCard title={S.PC_REVISIONS} iconName={IconName.Convert} defaultOpen={true}>
              {proposalID !== "new" && (
                <Container>
                  <div className="card-file-list-container">
                    {revisions.length > 0 ? (
                      revisions.map((r, i) => {
                        const note = mapProposalRevisionToNote(r, revisions.length - i - 0, ProposalRevisionItem);
                        return (
                          <div key={note.id}>
                            <Row className="card-row card-table-header">
                              <Col>{note.title}</Col>
                            </Row>
                            <Row className="card-separator" />
                            <Row className="card-row proposal-widgets-row">
                              <Col>{note.renderedDescription}</Col>
                            </Row>
                          </div>
                        );
                      })
                    ) : (
                      <Row className="card-row proposal-widgets-row">
                        <Col>{S.PC_NO_REVISIONS_AVAILABLE}</Col>
                      </Row>
                    )}
                  </div>
                  <div className="proposal-widgets-buttons">
                    <button
                      className="button-v2 button-v2__secondary small"
                      onClick={() => this.setState({ newRevisionPrompt: true })}>
                      {S.PC_NEW_REVISION}
                    </button>
                    {this.state.newRevisionPrompt && (
                      <PromptModal
                        open={true}
                        title={S.PC_NEW_REVISION_CONFIRM}
                        confirmText={S.PC_NEW_REVISION_SAVE}
                        rejectText={S.PC_NEW_REVISION_CANCEL}
                        onConfirm={async () => {
                          this.setLoading(true);
                          try {
                            await this.addRevision();
                          } finally {
                            this.setLoading(false);
                            this.setState({ newRevisionPrompt: false });
                          }
                        }}
                        onReject={() => this.setState({ newRevisionPrompt: false })}>
                        <Typography variant="h6">{S.PC_NEW_REVISION_WARNING}</Typography>
                      </PromptModal>
                    )}
                  </div>
                </Container>
              )}
            </CollapsibleCard>
            <CollapsibleCard title={S.PC_DISCLAIMER} iconName={IconName.ProposalBuilderDisclaimer} defaultOpen={true}>
              <Container>
                <FinalForm
                  onSubmit={async values => {
                    this.setLoading(true);
                    try {
                      await this.saveDisclaimer(values);
                    } finally {
                      this.setLoading(false);
                    }
                  }}
                  initialValues={disclaimerForm}>
                  {formProps => {
                    return (
                      <form onSubmit={formProps.handleSubmit}>
                        <TextField name="disclaimer" disabled={isNew} placeholder="Enter your text here" multiline />
                        <div className="proposal-widgets-buttons">
                          <button disabled={isNew} className="button-v2 button-v2__secondary small">
                            {S.PC_SAVE_DISCLAIMER_BUTTON}
                          </button>
                        </div>
                      </form>
                    );
                  }}
                </FinalForm>
              </Container>
            </CollapsibleCard>
            <CollapsibleCard title={S.PC_PROPOSAL_NOTES} iconName={IconName.BrokerBuilderComments} defaultOpen={true}>
              {proposalID !== "new" && (
                <Container>
                  <div className="card-file-list-container">
                    {notes && notes.length > 0 ? (
                      notes.map(note => (
                        <div key={note.id} className="proposal-editable-notes-widget">
                          <FormModal
                            values={note}
                            onSubmit={async values => {
                              this.setLoading(true);
                              try {
                                await this.editNote(note.id, values.title, values.description);
                              } finally {
                                this.setLoading(false);
                              }
                            }}
                            onClose={() => this.setLoading(false)}
                            launcher={
                              <div onClick={() => this.setLoading(true)}>
                                <Row className="card-row card-table-header">
                                  <Col>{note.title}</Col>
                                </Row>
                                <Row className="card-separator" />
                                <Row className="card-row proposal-widgets-row proposal-notes-description-row">
                                  <Col>{note.description}</Col>
                                </Row>
                              </div>
                            }>
                            <NewNoteForm isEdit={true} />
                          </FormModal>
                        </div>
                      ))
                    ) : (
                      <Row className="card-row proposal-widgets-row">
                        <Col>{S.PC_NO_PROPOSAL_NOTES_AVAILABLE}</Col>
                      </Row>
                    )}
                  </div>
                  <FormModal
                    onSubmit={async values => {
                      if (!values.title.trim().length && !values.description.trim().length) {
                        return;
                      }
                      this.setLoading(true);
                      try {
                        await this.addNote(values.title, values.description);
                      } finally {
                        this.setLoading(false);
                      }
                    }}
                    launcher={
                      <div className="proposal-widgets-buttons">
                        <button disabled={isNew} className="button-v2 button-v2__secondary small">
                          {S.PC_NEW_NOTE}
                        </button>
                      </div>
                    }>
                    <NewNoteForm />
                  </FormModal>
                </Container>
              )}
            </CollapsibleCard>
            <CollapsibleCard title={S.PC_BROKER_NOTES} iconName={IconName.ProposalBuilderAgentNotes} defaultOpen={true}>
              <Container>
                <div className="card-file-list-container">
                  {brokerNotes && brokerNotes.length > 0 ? (
                    brokerNotes.map(note => (
                      <Row key={note.id} className="card-row proposal-notes-description-row card-container-scrollbar">
                        <Col>{note.description}</Col>
                      </Row>
                    ))
                  ) : (
                    <Row className="card-row">
                      <Col>{S.PC_NO_BROKER_NOTES_AVAILABLE}</Col>
                    </Row>
                  )}
                </div>
              </Container>
            </CollapsibleCard>

            <AnalyzerLinks />

            <QuestionAnswersStatusTracker />

            {isAdmin ? (
              <CollapsibleCard title={S.PC_DELETE} iconName={IconName.DeleteFileGold} defaultOpen={true}>
                <Container>
                  <div className="card-file-list-container">
                    <button
                      className="button-v2 button-v2__secondary small"
                      onClick={e => {
                        e.stopPropagation();
                        this.setState({ deleteProposalPrompt: true });
                      }}>
                      {S.PC_DELETE_BUTTON}
                    </button>
                    {this.state.deleteProposalPrompt && (
                      <PromptModal
                        open={this.state.deleteProposalPrompt}
                        title={S.PC_CONFIRM_TITLE}
                        confirmText={S.PC_CONFIRM_DELETE}
                        rejectText={S.PC_CONFIRM_CANCEL}
                        onConfirm={async () => {
                          await removeProposal(proposalID);
                          this.setState({ deleteProposalPrompt: false });
                          this.props.history.push("/proposals/all");
                        }}
                        onReject={() => this.setState({ deleteProposalPrompt: false })}>
                        <Typography variant="h6">{S.PC_CONFIRM_DELETE_MESSAGE}</Typography>
                      </PromptModal>
                    )}
                  </div>
                </Container>
              </CollapsibleCard>
            ) : null}
          </aside>
        </main>

        <Toast
          message={S.LOADING}
          open={this.state.isToastOpen}
          onClose={() => this.setState({ isToastOpen: false })}
        />
        <Toast message={S.LOADING} open={this.state.loading} onClose={() => {}} />
      </div>
    );
  }
}

const mapStateToProps = (state: IRootState) => ({
  auth: state.auth,
  proposalNotes: state.proposalNotes,
  proposalMap: state.proposalOptions.proposalMap,
  disclaimerMap: state.proposalDisclaimer.map,
  proposalRevisions: state.proposalRevisions,
  isSubmitting: state.proposalOptions.isSubmitting,
  isSavingClone: state.proposalOptions.isSavingClone,
});

const mapDispatchToProps = {
  downloadAnalyzerProposal,
  downloadAnalyzer,
  downloadEqualizer,
  downloadEqualizerProposal,
  addProposalNote,
  addProposalRevision,
  fetchProposalNotes,
  editProposalNote,
  fetchProposalRevisions,
  fetchDisclaimer,
  updateDisclaimer,
  removeProposal,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProposalContainer));
